import { fromOverheadsDTO } from "../dto/overheadsMapper";
import { compileEmployeesFromCollections, prepareLoadedEmployees } from "./employeesUtils";

const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
const hasValue = (value) => value !== null && value !== undefined && value !== "";
const sameId = (left, right) => hasValue(left) && hasValue(right) && String(left) === String(right);
const labelOf = (item) => item?.label ?? item?.name ?? item?.value ?? item?.nogaCode ?? item?.id ?? "";
const withLabel = (items) =>
  asArray(items).map((item) => {
    const source = item && typeof item === "object" ? item : { id: item };
    const label = String(labelOf(source));
    return { ...source, name: source.name ?? label, label: source.label ?? label };
  });
const sortByName = (left, right) => String(left?.name ?? "").localeCompare(String(right?.name ?? ""));

export const formatSurveyData = async (rawData, translate, engine) => {
  const source = rawData || {};
  const survey = source.survey || null;
  const companyInput = asArray(source.companyInput).find((input) => sameId(input?.surveyId, survey?.id)) || null;
  const companyDetail = asArray(source.companyDetail).find((detail) => sameId(detail?.companyInputId, companyInput?.id)) || null;
  const nogaList = withLabel(source.nogaList);
  const cantonList = withLabel(source.cantonList);
  const officeSizeList = withLabel(source.officeSizeList);
  const selectedCantonRecord = asArray(source.selectedCanton)[0] || null;
  const companyId = selectedCantonRecord?.id ?? null;
  const selectedNoga = nogaList.find((item) => sameId(item.id, companyDetail?.nogaId)) || null;
  const selectedCanton = cantonList.find((item) => sameId(item.id, selectedCantonRecord?.cantonId)) || null;
  const selectedOfficeSize = officeSizeList.find((item) => sameId(item.id, companyDetail?.officeSizeId)) || null;
  const male = translate("benchmarking.survey.employee.option.male");
  const female = translate("benchmarking.survey.employee.option.female");
  const permanent = translate("benchmarking.survey.employee.entry-form.employmentType.permanent");
  const hourly = translate("benchmarking.survey.employee.entry-form.employmentType.hourly");
  const dropdownOptions = {
    allEmployeeFunctions: withLabel(source.allEmployeeFunctions).sort(sortByName),
    employeeFunctions: withLabel(source.employeeFunctions).sort(sortByName),
    employmentLevels: withLabel(source.employmentLevels),
    employmentStatuses: withLabel(source.employmentStatuses),
    employeeSexes: [
      { id: "m", name: male, label: male },
      { id: "f", name: female, label: female },
    ],
    employmentTypes: [
      { id: "PERMANENT_POSITION", name: permanent, label: permanent },
      { id: "HOURLY_RATE", name: hourly, label: hourly },
    ],
  };
  const employees = await prepareLoadedEmployees(compileEmployeesFromCollections(source), dropdownOptions, engine);

  const previousSurveys = await Promise.all(
    asArray(source.previousSurveys).map(async (previous) => ({
      surveyId: previous.surveyId,
      surveyYear: previous.surveyYear ?? null,
      companyInputId: previous.companyInputId ?? previous.companyInput?.id ?? null,
      companyInput: previous.companyInput || null,
      companyDetail: previous.companyDetail || null,
      // Preserve the historical endpoint tree. Its parent/children hierarchy
      // is the source of truth for the test-only legacy AccordionFIBU mapper.
      companyOverheads: previous.companyOverheads || [],
      employees: await prepareLoadedEmployees(compileEmployeesFromCollections(previous), dropdownOptions, engine),
    }))
  );

  return {
    ...source,
    survey,
    companyInput,
    companyDetail,
    companyOverheads: fromOverheadsDTO(source.companyOverheads || [], translate),
    employees,
    previousSurveys,
    nogaList,
    cantonList,
    officeSizeList,
    selectedNoga,
    selectedCanton,
    selectedOfficeSize,
    companyId,
    ...dropdownOptions,
    reviewPhase: Boolean(companyInput?.inputComplete && companyInput?.accepted === null),
    surveyConcluded: Boolean(companyInput?.accepted),
    isSurveyFinished: Boolean(survey?.finishedAt),
  };
};
